import { ContentPlacement } from '@today/types'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement, SubmitEvent } from 'react'
import { ErrorText, SelectInput } from './content-fields'
import type { ContentOption, PlacementView, Translate } from './content-types'

/**
 * Props for {@link AttachDialogView}.
 *
 * @property candidates - Texts with a published or approved localization, not yet attached.
 * @property product - Product scope: placement and "Replaces" are asked.
 * @property replaceOptions - Inherited tenant texts not yet replaced.
 */
export interface AttachDialogViewProps {
	t: Translate
	isOpen: boolean
	product: boolean
	search: string
	candidates: ContentOption[]
	textId: string
	placement: PlacementView
	replacesTextId: string
	replaceOptions: ContentOption[]
	error: string | null
	isBusy: boolean
	onSearch: (q: string) => void
	onChange: (field: 'textId' | 'placement' | 'replacesTextId', value: string) => void
	onConfirm: () => void
	onCancel: () => void
}

function ProductChoices({ props }: { props: AttachDialogViewProps }): ReactElement {
	const { t } = props
	return (
		<>
			{props.product ? (
				<>
					<SelectInput
						label={t('assignments.placement')}
						value={props.placement}
						options={Object.values(ContentPlacement).map((p) => ({ id: p, label: t(`placement.${p}`) }))}
						onChange={(v) => props.onChange('placement', v)}
					/>
					<SelectInput
						label={t('assignments.replaces')}
						value={props.replacesTextId}
						emptyLabel={t('assignments.replacesNone')}
						options={props.replaceOptions}
						onChange={(v) => props.onChange('replacesTextId', v)}
					/>
				</>
			) : null}
		</>
	)
}

/** Attach a text: a searchable picker, then placement and replacement for products (organism). */
export function AttachDialogView(props: AttachDialogViewProps): ReactElement {
	const { t } = props
	const search = (event: SubmitEvent<HTMLFormElement>) => {
		event.preventDefault()
		props.onSearch(String(new FormData(event.currentTarget).get('q') ?? '').trim())
	}
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onCancel())}
			title={t('assignments.attachTitle')}
			labels={{ close: t('actions.close') }}
		>
			<div className="flex flex-col gap-3">
				<ErrorText message={props.error} />
				<form onSubmit={search} className="flex items-end gap-2">
					<label className="flex flex-col gap-1.5 text-ds-sm text-ds-text-muted">
						{t('assignments.search')}
						<input
							name="q"
							type="search"
							defaultValue={props.search}
							className="h-10 rounded-ds-md border border-ds-border px-3"
						/>
					</label>
				</form>
				<SelectInput
					label={t('assignments.text')}
					value={props.textId}
					emptyLabel={t('form.choose')}
					options={props.candidates}
					onChange={(v) => props.onChange('textId', v)}
				/>
				<ProductChoices props={props} />
				<div className="flex gap-2">
					<Button isDisabled={props.isBusy || !props.textId} onPress={props.onConfirm}>
						{t('assignments.attach')}
					</Button>
					<Button variant="muted" onPress={props.onCancel}>
						{t('actions.cancel')}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
